import { injectable } from 'inversify-props';
import axios, { AxiosPromise, AxiosInstance, AxiosError, AxiosRequestConfig } from 'axios';
import { Role } from '@models';
import { UserManager, WebStorageStateStore, User as OidcUser, UserManagerSettings } from 'oidc-client';
import IAuthService from '../IAuthService';


@injectable()
export default class AuthService implements IAuthService {
    private userManager: UserManager;
    private baseUrl: string
    public ready: Promise<any>;

    constructor() {
        this.ready = new Promise((resolve, reject) => {
            fetch('/api/environment/getEnvironmentVairables',
                {
                    method: "POST",
                    body: JSON.stringify(
                        ["OAuth2:BaseUrl", "OAuth2:Authority"]
                    ),
                    headers: {
                        "Content-Type": "application/json"
                    }
                }).then(response => response.json())
                .then(response => {
                    if (response["OAuth2:Authority"] && response["OAuth2:BaseUrl"]) {
                        this.baseUrl = response["OAuth2:BaseUrl"];
                        const settings: UserManagerSettings = this.getUserManagerSettings(response["OAuth2:Authority"]);

                        this.userManager = new UserManager(settings);

                        this.userManager.events.addSilentRenewError((e) => {
                            console.error("silent renew error:", e.message);
                        });
                        resolve(undefined);
                    }
                    else {
                        console.error("Could not fetch baseurl and/or authority settings");
                    }
                }
            ).catch(
                error => {
                    console.error("Error - could not fetch AuthService related environtment settings", error);
                }
            );

        });
    }

    getUserManagerSettings(authority: string): UserManagerSettings {
        const settings: UserManagerSettings = {
            userStore: new WebStorageStateStore({ store: window.localStorage }),
            authority: authority,
            client_id: 'adnami_suite_vue_app',
            redirect_uri: window.location.origin + '/callback',
            silent_redirect_uri: window.location.origin + '/silent-renew',
            automaticSilentRenew: true,
            includeIdTokenInSilentRenew: true,
            response_type: 'code',
            scope: 'openid profile roles suite:client_api',
            post_logout_redirect_uri: window.location.origin,
            filterProtocolClaims: true,
            accessTokenExpiringNotificationTime: 60,
        };

        return settings;
    }

    getUser(): Promise<OidcUser | null> {
        return this.ready.then(res => {
            return this.userManager.getUser();
        });
    }

    login(): Promise<void> {
        return this.ready.then(res => {
            return this.userManager.signinRedirect();
        });
    }

    loginSilent(): Promise<OidcUser> {
        return this.ready.then(res => {
            return this.userManager.signinRedirectCallback();
        });
    }

    clearUserAndRedirectToLogin(): Promise<void> {

        this.userManager.clearStaleState();
        this.userManager.removeUser();

        return this.ready.then(res => {
            return this.userManager.signinRedirect();
        });
    }

    signinSilentCallback(url?: string): Promise<OidcUser> {
        return this.ready.then(res => {
            return this.userManager.signinSilentCallback(url);
        });
    }

    handleUser(user) {
        return this.ready.then(res => {
            this.userManager.storeUser(user);
        });
    }

    logout = async () => {
        
            const user = await this.getUser();

            if (!user) {
                // This should never be possible. We should not be able to get here. 
                return this.userManager.signoutRedirect();
            } else {
                this.userManager.signoutRedirect({
                    id_token_hint: user.id_token,
                });
            }
            this.userManager.clearStaleState();
            this.userManager.removeUser();
        
    }

    getAccessToken(): Promise<string> {
        return this.ready.then(res => {
            return this.userManager.getUser().then((data: any) => {
                return data.access_token;
            });
        });
    }

    userHasToken(): Promise<boolean> {
        return this.ready.then(res => {
            return this.userManager.getUser().then((data: any) => {
                return data && data.access_token && !data.expired;
            });
        });
        
    }

    userHasRole(adGroupLevel: Role): Promise<boolean> {
        return this.ready.then(res => {
            return this.userManager.getUser().then((user: OidcUser) => {
                let result = false;
                if (user && user.profile.role) {
                    if (Array.isArray(user.profile.role)) {
                        result = user.profile.role.some(r => r === Role.Administrator || r === adGroupLevel);
                    } else {
                        result = user.profile.role === Role.Administrator || user.profile.role === adGroupLevel;
                    }

                }

                return result;
            });
        });
    }

    getAxiosInstance(): Promise<AxiosInstance> {
        return this.getAccessToken().then((token: any) => {
            if (token) {
                return axios.create({
                    headers: { 'Authorization': 'Bearer ' + token }
                });
            } else {
                return undefined;
            }
        });
    }

    get(ur: string): AxiosPromise {
        return this.getAxiosInstance().then(axiosInstance => {
            if (axiosInstance) {
                return axiosInstance.get(ur).then(response => {
                    return response;
                }).catch(error => {
                    var e = error as AxiosError;

                    //Unauthorized 
                    if (e.response.status === 401) {
                        this.logout();
                    } else {
                        return error;
                    }
                });
            } else {
                this.logout();

                return new Promise((resolve, reject) => {
                    reject("No axios instance!");
                }) as AxiosPromise;
            }
        })
    }

    getV2(ur: string): AxiosPromise {
        return this.getAxiosInstance().then(axiosInstance => {
            if (axiosInstance) {
                return axiosInstance.get(ur).then(response => {
                    return response;
                }).catch(error => {
                    var e = error as AxiosError;

                    //Unauthorized 
                    if (e.response.status === 401) {
                        this.logout();
                    } else {
                        return error.response;
                    }
                });
            } else {
                this.logout();

                return new Promise((resolve, reject) => {
                    reject("No axios instance!");
                }) as AxiosPromise;
            }
        })


    }

    post(url: string, data: any, axiosRequestConfig: AxiosRequestConfig = null, useAuthServer: boolean = false): AxiosPromise {
        return this.getAxiosInstance().then(axiosInstance => {
            if (axiosInstance) {
                return axiosInstance.post(useAuthServer ? this.baseUrl + url : url, data, axiosRequestConfig).then(response => {
                    return response;
                }).catch(error => {
                    var e = error as AxiosError;

                    //Unauthorized 
                    if (e.response.status === 401) {
                        this.logout();
                    } else {
                        return error.response;
                    }
                });
            } else {
                this.logout();

                return new Promise((resolve, reject) => {
                    reject("No axios instance!");
                }) as AxiosPromise;
            }
        })
    }

    put(url: string, data: any): AxiosPromise {
        return this.getAxiosInstance().then(axiosInstance => {
            if (!axiosInstance) {
                this.logout();

                return new Promise((resolve, reject) => {
                    reject("No axios instance!");
                }) as AxiosPromise;
            }

            const result = axiosInstance.put(url, data).then(response => {
                return response;
            }).catch(error => {
                var e = error as AxiosError;

                //Unauthorized 
                if (e.response.status === 401) {
                    this.logout();
                } else {
                    return error;
                }
            })

            return result;
        })
    }
}
